/**
 * Auth Zustand store.
 *
 * Manages the currently-authenticated user and token lifecycle.
 */

import type { User } from '@burmazon/shared';
import { create } from 'zustand';
import { persist } from 'zustand/middleware';

import { apiPost, setAccessToken, subscribeToForceLogout } from '../lib/api';

interface AuthState {
  user: Omit<User, 'createdAt' | 'updatedAt'> | null;
  isAuthenticated: boolean;
  isLoading: boolean;

  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  setUser: (user: AuthState['user']) => void;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, _get) => ({
      user: null,
      isAuthenticated: false,
      isLoading: false,

      setUser: (user) => set({ user, isAuthenticated: !!user }),

      login: async (email, password) => {
        set({ isLoading: true });
        try {
          const response = await apiPost<{
            user: AuthState['user'];
            tokens: { accessToken: string; refreshToken: string };
          }>('/auth/login', { email, password });

          setAccessToken(response.tokens.accessToken);
          localStorage.setItem('refreshToken', response.tokens.refreshToken);
          set({ user: response.user, isAuthenticated: true });
        } finally {
          set({ isLoading: false });
        }
      },

      logout: async () => {
        const refreshToken = localStorage.getItem('refreshToken');
        try {
          await apiPost('/auth/logout', { refreshToken });
        } catch {
          // Ignore errors on logout — clear local state regardless
        }
        setAccessToken(null);
        localStorage.removeItem('refreshToken');
        set({ user: null, isAuthenticated: false });
      },
    }),
    {
      name: 'burmazon-auth',
      // Only persist the user object, not the loading state
      partialize: (state) => ({ user: state.user, isAuthenticated: state.isAuthenticated }),
    },
  ),
);

// When the token refresh fails, clear auth state so React Router's ProtectedRoute
// handles redirects. Guest-friendly pages (e.g. /checkout) stay on screen.
subscribeToForceLogout(() => {
  useAuthStore.setState({ user: null, isAuthenticated: false });
});
